import {
  DatabaseOutlined,
  DollarOutlined,
  FileTextOutlined,
  PieChartOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Button, Card, Input, Select, Space, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { DaoPtoProjectItemType } from 'Modules'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BREADCRUMBS } from 'constants/breadcrumb'
import {
  DAO_ASSET_STATUS_FILTER_OPTIONS,
  DAO_DATE_FORMAT,
  DAO_PAGE_SIZE_OPTIONS,
  DAO_SELLING_VERSION_FILTER_OPTIONS,
  DaoAssetStatusEnum,
  DaoSellingVersionEnum,
} from 'constants/dao-voting'
import { useDaoOverview } from 'hooks/dao-voting/use-dao-overview'
import { useDaoPtoProjects } from 'hooks/dao-voting/use-dao-pto-projects'
import { AssetStatusTag, ProposalStatusTag } from 'modules/dao-voting/dao-status-tag'
import ProjectAvatar from 'modules/dao-voting/project-avatar'
import { PAGES } from 'routes'

import './style.scss'

/**
 * DAO Voting List (SRS 5.1) - overview stat cards + PTO project list.
 */
const DaoVotingList = () => {
  const navigate = useNavigate()

  const [searchValue, setSearchValue] = useState('')
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<DaoAssetStatusEnum | ''>('')
  const [sellingVersion, setSellingVersion] = useState<DaoSellingVersionEnum | ''>('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const { stats } = useDaoOverview()
  const { projects, totalResults, isLoading } = useDaoPtoProjects({
    page,
    limit: pageSize,
    search: keyword,
    status,
    sellingVersion,
  })

  const onSearch = () => {
    setPage(1)
    setKeyword(searchValue.trim())
  }

  const statCards = [
    {
      title: 'Total PTO Projects with DAO',
      value: stats?.totalProjects ?? 0,
      icon: <DatabaseOutlined />,
      className: 'blue',
    },
    {
      title: 'Active Votes',
      value: stats?.activeVotes ?? 0,
      icon: <PieChartOutlined />,
      className: 'purple',
    },
    {
      title: 'Total Proposals',
      value: stats?.totalProposals ?? 0,
      icon: <FileTextOutlined />,
      className: 'gold',
    },
    {
      title: 'Pools Funded',
      value: stats?.poolsFunded ?? 0,
      icon: <DollarOutlined />,
      className: 'green',
    },
  ]

  const columns: ColumnsType<DaoPtoProjectItemType> = [
    {
      title: 'PTO Project',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (name, record) => (
        <Space>
          <ProjectAvatar name={name} src={record.thumbnail} />
          <span style={{ fontWeight: 600 }}>{name}</span>
        </Space>
      ),
    },
    {
      title: 'Asset Status',
      dataIndex: 'assetStatus',
      key: 'assetStatus',
      render: (assetStatus) => <AssetStatusTag status={assetStatus} />,
    },
    {
      title: 'Current Proposal',
      key: 'currentProposal',
      render: (_, record) => {
        if (!record.currentProposal) return '--'
        return (
          <div>
            <div>{record.currentProposal.title}</div>
            <ProposalStatusTag status={record.currentProposal.status} />
          </div>
        )
      },
    },
    {
      title: 'Last Updated',
      dataIndex: 'lastUpdatedAt',
      key: 'lastUpdatedAt',
      render: (value) => (value ? dayjs(value).format(DAO_DATE_FORMAT) : '--'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          className="dao-view-detail-btn"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`${PAGES.DAO_VOTING}/${record._id}`)
          }}
        >
          View Detail
        </Button>
      ),
    },
  ]

  return (
    <div className="main-section dao-voting-page">
      <Breadcrumb additionalItems={[BREADCRUMBS.DAO_VOTING_NO_LINK]} />

      <div className="main-title">DAO Voting</div>
      <div className="dao-page-subtitle">Manage DAO governance for PTO projects</div>

      <div className="dao-section-title">DAO Voting Overview</div>
      <div className="dao-stat-cards">
        {statCards.map((item) => (
          <Card key={item.title} className="dao-stat-card">
            <div className="dao-stat-content">
              <div>
                <div className="dao-stat-title">{item.title}</div>
                <div className="dao-stat-value">{item.value}</div>
              </div>
              <span className={`dao-stat-icon ${item.className}`}>{item.icon}</span>
            </div>
          </Card>
        ))}
      </div>

      <Card className="dao-list-card">
        <div className="dao-section-title" style={{ marginTop: 0 }}>
          PTO Project List
        </div>
        <div className="dao-list-filters">
          <Input
            prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.3)' }} />}
            placeholder="Search by project name"
            maxLength={255}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onPressEnter={onSearch}
            onBlur={onSearch}
            style={{ width: 320 }}
            allowClear
          />
          <Select
            value={status}
            onChange={(value) => {
              setPage(1)
              setStatus(value)
            }}
            options={DAO_ASSET_STATUS_FILTER_OPTIONS}
            style={{ width: 190 }}
          />
          <Select
            value={sellingVersion}
            onChange={(value) => {
              setPage(1)
              setSellingVersion(value)
            }}
            options={DAO_SELLING_VERSION_FILTER_OPTIONS}
            style={{ width: 170 }}
          />
        </div>
        <div className="dao-showing-text">
          Showing <b>{projects.length}</b> of <b>{totalResults}</b> projects
        </div>
        <div className="table-row">
          <Table
            columns={columns}
            dataSource={projects}
            rowKey={(record) => record._id}
            loading={isLoading}
            onRow={(record) => ({
              onClick: () => navigate(`${PAGES.DAO_VOTING}/${record._id}`),
              style: { cursor: 'pointer' },
            })}
            pagination={{
              current: page,
              pageSize,
              total: totalResults,
              showSizeChanger: true,
              pageSizeOptions: DAO_PAGE_SIZE_OPTIONS,
              onChange: (nextPage, nextSize) => {
                setPage(nextSize !== pageSize ? 1 : nextPage)
                setPageSize(nextSize)
              },
            }}
            scroll={{ x: 900 }}
          />
        </div>
      </Card>
    </div>
  )
}

export default DaoVotingList
